import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    ArrowPathIcon,
    ExclamationTriangleIcon,
    NoSymbolIcon,
    PencilIcon
} from '@heroicons/react/24/outline';
import {
    useCharge,
    useProcessCharge,
    useCancelCharge,
    useRetryCharge,
    useUpdateCharge
} from '../../hooks/useBilling';
import { useMyPermissions } from '../../hooks/useMyPermissions';
import { useToast } from '../../providers/ToastProvider';
import { apiErrorMessage } from '../../utils/apiError';
import { fetchPatientsPage } from '../../services/patientsV2';
import { CreateChargeModal } from './CreateChargeModal';
import { Drawer, DrawerSection, KVRow } from './Drawer';
import { EventTimeline } from './EventTimeline';
import { PatientLink } from './PatientLink';
import { SearchSelect } from './SearchSelect';
import { StatusBadge, SourceChip } from './StatusBadge';
import { UnusedCreditsWarning } from './UnusedCreditsWarning';
import { allocateCredits, creditUses, settlesWithCredits } from './creditAllocation';
import { AppliedOneTimeDiscounts } from './ChargeCredits';
import { chargeResultToast } from './chargeResultToast';
import { formatMoneyCents, formatMoneyDollars as money } from '../../utils/money';
import { isOrthopulseProductName } from '../../services/orthopulseOrder';
import { ChargePurchaseOrders } from './purchase-orders/ChargePurchaseOrders';
import { SendPurchaseOrderModal } from './purchase-orders/SendPurchaseOrderModal';
import {
    fetchPracticeProductPricing,
    ruleBreakdown,
    rulesForVersion,
    type PricedProduct
} from '../../services/practiceProductPricing';
import { fetchCharges } from '../../services/billing';
import { fetchTimeline, fetchJourney } from '../../services/timeline';
import { TimelineRailEvent } from '../patient-files/timeline-rail/TimelineRailEvent';
import { JourneyView } from '../patient-files/timeline-rail/JourneyView';
import {
    TimelineModeToggle,
    type TimelineMode
} from '../patient-files/timeline-rail/TimelineModeToggle';
import type { ChargeDetail, ChargeLineItem } from '../../services/billing';
import { useEscapeKey } from '../../hooks/useEscapeKey';

function fmtDate(iso: string | null): string {
    return iso
        ? new Date(iso).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
          })
        : '-';
}

function fmtDateTime(iso: string): string {
    return new Date(iso).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
    });
}

function shortRef(id: string): string {
    return id.slice(0, 8).toUpperCase();
}

// Whole days the payment landed after the due date; 0 or negative means on time.
function daysLate(dueDate: string, paidAt: string): number {
    const msPerDay = 24 * 60 * 60 * 1000;
    return Math.floor((new Date(paidAt).getTime() - new Date(dueDate).getTime()) / msPerDay);
}

function discountAmount(charge: ChargeDetail): number {
    return Number(charge.baseAmount) - Number(charge.totalAmount);
}

function discountLabel(charge: ChargeDetail): string {
    if (charge.discountType === 'percentage') return `${Number(charge.discountValue)}%`;
    return 'flat';
}

// While a charge is pending the patient can be linked or changed in place;
// an errored charge is locked like an invoiced one (cancel and re-create instead).
function EditablePatient({
    charge,
    canEditBilling
}: {
    charge: ChargeDetail;
    canEditBilling: boolean;
}) {
    const { showToast } = useToast();
    const { mutateAsync: update, isPending } = useUpdateCharge();
    const [editing, setEditing] = useState(false);
    const [patientId, setPatientId] = useState(charge.patientId ?? '');
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => clearTimeout(handle);
    }, [search]);

    const { data: patientsData, isLoading } = useQuery({
        queryKey: ['v2/patients', charge.practice.name, debouncedSearch, 'charge-edit'],
        queryFn: () =>
            fetchPatientsPage({
                page: 1,
                limit: 100,
                sort: 'patient',
                order: 'asc',
                practice: [charge.practice.name],
                search: debouncedSearch || undefined
            }),
        enabled: editing,
        staleTime: 60 * 1000
    });
    // Pin the picked patient so it stays displayable after the search resets
    // (the fetched page only holds the first 100 patients alphabetically).
    const [pickedPatient, setPickedPatient] = useState<{ value: string; label: string } | null>(
        charge.patientId
            ? { value: charge.patientId, label: charge.patientName ?? `#${charge.patientId}` }
            : null
    );
    const options = (patientsData?.data ?? []).map((p) => ({ value: p.id, label: p.name }));
    if (pickedPatient && !options.some((o) => o.value === pickedPatient.value)) {
        options.unshift(pickedPatient);
    }
    const handlePick = (id: string) => {
        setPatientId(id);
        setPickedPatient(options.find((o) => o.value === id) ?? null);
    };

    const canEdit = canEditBilling && charge.status === 'pending';

    const handleSave = async () => {
        try {
            await update({ id: charge.id, changes: { patientId: patientId || null } });
            showToast(patientId ? 'Patient linked to charge' : 'Patient unlinked', 'success');
            setEditing(false);
        } catch (error) {
            showToast(apiErrorMessage(error, 'Failed to update charge'), 'error');
        }
    };

    if (!editing) {
        return (
            <span className="inline-flex items-center gap-2">
                <PatientLink patientId={charge.patientId} patientName={charge.patientName}>
                    {charge.patientName ?? (charge.patientId ? `#${charge.patientId}` : '-')}
                </PatientLink>
                {canEdit && (
                    <button
                        type="button"
                        onClick={() => {
                            setPatientId(charge.patientId ?? '');
                            setEditing(true);
                        }}
                        className="w-6 h-6 rounded hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors"
                        aria-label="Link patient"
                    >
                        <PencilIcon className="h-3.5 w-3.5" />
                    </button>
                )}
            </span>
        );
    }

    return (
        <span className="flex items-center gap-2 w-full max-w-[320px]">
            <span className="flex-1 min-w-0">
                <SearchSelect
                    value={patientId}
                    options={options}
                    onChange={handlePick}
                    onSearchChange={setSearch}
                    placeholder={isLoading ? 'Loading patients…' : 'Select patient…'}
                    searchPlaceholder="Search patients..."
                />
            </span>
            <button
                type="button"
                onClick={() => {
                    void handleSave();
                }}
                disabled={isPending}
                className="bg-gray-800 text-white rounded-lg px-3 py-1.5 text-xs font-semibold hover:bg-gray-900 transition-colors disabled:opacity-50 shrink-0"
            >
                {isPending ? 'Saving…' : 'Save'}
            </button>
            <button
                type="button"
                onClick={() => setEditing(false)}
                className="text-gray-500 text-xs font-semibold px-2 py-1.5 rounded-lg hover:bg-gray-100 transition-colors shrink-0"
            >
                Cancel
            </button>
        </span>
    );
}

// ── Sections ────────────────────────────────────────────────────────────

function ErrorAlert({
    message,
    onRetry,
    isRetrying,
    canEditBilling
}: {
    message: string;
    onRetry: () => void;
    isRetrying: boolean;
    canEditBilling: boolean;
}) {
    return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex flex-col gap-3">
            <div className="flex gap-2.5">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600 shrink-0" />
                <div className="min-w-0">
                    <p className="text-sm font-semibold text-red-800">Processing failed</p>
                    <p className="text-sm text-red-700 mt-1 break-words">{message}</p>
                </div>
            </div>
            {canEditBilling && (
                <div>
                    <button
                        type="button"
                        onClick={onRetry}
                        disabled={isRetrying}
                        className="flex items-center gap-2 bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 font-semibold text-sm transition-colors disabled:opacity-50"
                    >
                        <ArrowPathIcon className="h-4 w-4" />
                        {isRetrying ? 'Retrying…' : 'Retry charge'}
                    </button>
                </div>
            )}
        </div>
    );
}

function AmountRow({
    label,
    sub,
    value,
    minus,
    strong
}: {
    label: string;
    sub?: string;
    value: string;
    minus?: boolean;
    strong?: boolean;
}) {
    return (
        <div
            className={`flex items-center justify-between gap-3 ${strong ? 'pt-2.5 mt-1 border-t border-gray-200' : 'py-0.5'}`}
        >
            <span
                className={`text-sm min-w-0 truncate ${strong ? 'font-bold text-gray-900' : 'text-gray-500'}`}
                title={sub ? `${label} · ${sub}` : label}
            >
                {label}
                {sub && <span className="text-xs text-gray-400 font-normal"> · {sub}</span>}
            </span>
            <span
                className={`tabular-nums shrink-0 ${strong ? 'text-lg font-bold text-gray-900' : `text-sm ${minus ? 'text-red-600' : 'text-gray-800'}`}`}
            >
                {value}
            </span>
        </div>
    );
}

function ChargeBreakdown({ charge }: { charge: ChargeDetail }) {
    const hasDiscount = charge.discountType != null && charge.discountValue != null;
    const uses = creditUses(charge);
    const remainderCents = uses.reduce((sum, use) => sum + use.returnedCents, 0);
    return (
        <div className="border border-gray-200 rounded-lg p-4 flex flex-col">
            <AmountRow label="Line items total" value={money(charge.baseAmount)} />
            {hasDiscount && (
                <AmountRow
                    label="Discount"
                    sub={discountLabel(charge)}
                    value={`−${money(discountAmount(charge))}`}
                    minus
                />
            )}
            <AmountRow label="Charge total" value={money(charge.totalAmount)} strong />
            {uses.map(({ credit, usedCents }) => (
                <AmountRow
                    key={credit.id}
                    label="One-time discount applied"
                    sub={
                        usedCents === 0 ? `${credit.description} · not needed` : credit.description
                    }
                    value={`−${money(credit.amount)}`}
                    minus
                />
            ))}
            {charge.status === 'pending' && remainderCents > 0 && (
                <AmountRow
                    label="Remainder"
                    sub="returned as credit at processing"
                    value={`+${formatMoneyCents(remainderCents)}`}
                />
            )}
            <AmountRow label="Invoice total" value={money(charge.invoiceTotal)} strong />
        </div>
    );
}

// List price through the practice's current discount rules to the line's
// unit price - a stale historical price gets an explicit note instead of silence.
function LinePriceNarration({ item, priced }: { item: ChargeLineItem; priced: PricedProduct }) {
    const version = priced.versions.find((v) => v.id === item.productVersionId);
    if (!version) return null;
    const applied = rulesForVersion(priced.rules, version.id);
    const practiceCents = ruleBreakdown(version.priceCents, applied).reduce(
        (price, { deltaCents }) => price - deltaCents,
        version.priceCents
    );
    const unitCents = Math.round(Number(item.unitPrice) * 100);
    if (applied.length === 0 && unitCents === version.priceCents) return null;
    return (
        <div className="bg-gray-50 border border-gray-100 rounded-md px-3 py-2 mt-2 flex flex-col gap-0.5">
            <div className="flex items-center justify-between gap-3">
                <span className="text-[11px] text-gray-500">List price</span>
                <span className="text-[11px] text-gray-600 tabular-nums">
                    {formatMoneyCents(version.priceCents)}
                </span>
            </div>
            {ruleBreakdown(version.priceCents, applied).map(({ rule, deltaCents }) => (
                <div key={rule.id} className="flex items-center justify-between gap-3">
                    <span className="text-[11px] text-green-700 truncate">{rule.name}</span>
                    <span className="text-[11px] text-green-700 tabular-nums shrink-0">
                        −{formatMoneyCents(deltaCents)}
                    </span>
                </div>
            ))}
            <div className="flex items-center justify-between gap-3 border-t border-gray-200 pt-1">
                <span className="text-[11px] font-semibold text-gray-700">Practice price</span>
                <span className="text-[11px] font-semibold text-gray-900 tabular-nums">
                    {formatMoneyCents(practiceCents)}
                </span>
            </div>
            {unitCents !== practiceCents && (
                <span className="text-[10px] text-amber-600">
                    Charged at {money(item.unitPrice)} - manual override or pricing has changed
                    since.
                </span>
            )}
        </div>
    );
}

function LineItemsList({ charge }: { charge: ChargeDetail }) {
    // Current practice pricing, to narrate how each line's price was built.
    const { data: pricing } = useQuery({
        queryKey: ['v2/practices', charge.practice.id, 'product-pricing'],
        queryFn: () => fetchPracticeProductPricing(charge.practice.id),
        staleTime: 5 * 60 * 1000
    });
    const pricedByProductId = new Map((pricing ?? []).map((p) => [p.id, p]));

    return (
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
            {charge.lineItems.map((item) => {
                const priced = pricedByProductId.get(item.productId);
                return (
                    <div key={item.id} className="px-4 py-3">
                        <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-800">
                                    {item.productName}
                                    {item.versionLabel && item.versionLabel !== 'Standard' && (
                                        <span className="ml-1.5 text-xs font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
                                            {item.versionLabel}
                                        </span>
                                    )}
                                </p>
                                <p
                                    className="text-xs text-gray-500 mt-0.5 truncate"
                                    title={item.description}
                                >
                                    {item.description}
                                </p>
                            </div>
                            <div className="text-right shrink-0">
                                <p className="text-sm font-semibold text-gray-900 tabular-nums">
                                    {money(item.lineTotal)}
                                </p>
                                <p className="text-xs text-gray-400">
                                    {item.quantity} × {money(item.unitPrice)}
                                </p>
                            </div>
                        </div>
                        {priced && <LinePriceNarration item={item} priced={priced} />}
                    </div>
                );
            })}
        </div>
    );
}

// Every other charge of the same patient, newest first - surfaced right in
// the drawer so a duplicate charge is caught BEFORE it gets invoiced.
function PatientOtherCharges({ charge }: { charge: ChargeDetail }) {
    const navigate = useNavigate();
    const patientId = charge.patientId;
    const { data, isLoading } = useQuery({
        queryKey: ['v2/billing/charges', 'by-patient', patientId],
        queryFn: () =>
            fetchCharges({
                page: 1,
                limit: 25,
                patientId: patientId!,
                sort: 'date',
                order: 'desc'
            }),
        enabled: Boolean(patientId),
        staleTime: 60 * 1000
    });
    const others = (data?.data ?? []).filter((c) => c.id !== charge.id);

    if (isLoading) {
        return <p className="text-sm text-gray-400">Loading charges…</p>;
    }
    if (others.length === 0) {
        return <p className="text-sm text-gray-400">No other charges for this patient.</p>;
    }
    return (
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
            {others.map((other) => (
                <button
                    key={other.id}
                    type="button"
                    onClick={() => navigate(`/billing/charges/${other.id}`)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors"
                >
                    <span className="font-mono text-[12px] text-gray-700 shrink-0">
                        #{shortRef(other.id)}
                    </span>
                    <StatusBadge status={other.status} />
                    <span className="text-xs text-gray-500 truncate flex-1 min-w-0">
                        {other.productSummary ?? other.description ?? 'No description'}
                    </span>
                    <span className="text-right shrink-0">
                        <span className="block text-sm font-semibold text-gray-900 tabular-nums">
                            {money(other.totalAmount)}
                        </span>
                        <span className="block text-[11px] text-gray-400">
                            {fmtDate(other.createdAt)}
                        </span>
                    </span>
                </button>
            ))}
        </div>
    );
}

// The backend caps timeline pages at 100 rows; pull every page (sanity-capped)
// so the drawer shows the COMPLETE history, not a teaser.
const TIMELINE_PAGE_LIMIT = 100;
const TIMELINE_MAX_PAGES = 5;

async function fetchAllTimelineEvents(patientId: string) {
    const first = await fetchTimeline(patientId, { isLatest: true, limit: TIMELINE_PAGE_LIMIT });
    const events = [...first.data];
    const pages = Math.min(Math.ceil(first.total / TIMELINE_PAGE_LIMIT), TIMELINE_MAX_PAGES);
    if (pages > 1) {
        const rest = await Promise.all(
            Array.from({ length: pages - 1 }, (_, i) =>
                fetchTimeline(patientId, {
                    isLatest: true,
                    limit: TIMELINE_PAGE_LIMIT,
                    page: i + 2
                })
            )
        );
        for (const pageResponse of rest) {
            events.push(...pageResponse.data);
        }
    }
    return events;
}

// The linked patient's clinical/workflow history (Activity feed or Journey
// phases). The mode toggle lives in the drawer section's header, not here.
function PatientTimelinePreview({ patientId, mode }: { patientId: string; mode: TimelineMode }) {
    const { data, isLoading, isError } = useQuery({
        queryKey: ['patient-timeline', patientId, 'all'],
        queryFn: () => fetchAllTimelineEvents(patientId),
        staleTime: 60 * 1000
    });
    // Journey is fetched lazily, only once the toggle switches to it.
    const journeyQuery = useQuery({
        queryKey: ['patient-journey', patientId],
        queryFn: () => fetchJourney(patientId),
        staleTime: 60 * 1000,
        enabled: mode === 'journey'
    });

    const events = [...(data ?? [])].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    return (
        // Height-capped: long histories scroll inside the card instead of
        // stretching the drawer.
        <div className="border border-gray-200 rounded-lg max-h-[340px] overflow-y-auto scrollbar-hide px-4 pt-3 pb-1.5">
            {mode === 'journey' ? (
                <JourneyView
                    segments={journeyQuery.data ?? []}
                    isLoading={journeyQuery.isLoading}
                    isError={journeyQuery.isError}
                />
            ) : isLoading ? (
                <p className="text-sm text-gray-400 pb-2">Loading timeline…</p>
            ) : isError ? (
                <p className="text-sm text-gray-400 pb-2">Could not load the patient timeline.</p>
            ) : events.length === 0 ? (
                <p className="text-sm text-gray-400 pb-2">No timeline events for this patient.</p>
            ) : (
                <ol>
                    {events.map((event, index) => (
                        <TimelineRailEvent
                            key={event.id}
                            event={event}
                            isLast={index === events.length - 1}
                        />
                    ))}
                </ol>
            )}
        </div>
    );
}

function InvoicesList({ charge }: { charge: ChargeDetail }) {
    // Rows open the invoice's own drawer on the invoices page - a real route
    // change, so browser back returns here and forward re-opens the invoice.
    const navigate = useNavigate();
    if (charge.invoices.length === 0) {
        return (
            <div className="border border-dashed border-gray-200 rounded-lg px-4 py-5 text-center">
                <p className="text-sm text-gray-500">
                    {charge.status === 'pending'
                        ? 'No invoices yet. Process the charge to generate them.'
                        : 'No invoices.'}
                </p>
            </div>
        );
    }
    return (
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
            {charge.invoices.map((invoice) => (
                <button
                    key={invoice.id}
                    type="button"
                    onClick={() => navigate(`/billing/invoices/${invoice.id}`)}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors"
                >
                    <div className="flex items-center gap-2.5">
                        <span className="font-mono text-[12px] text-gray-800 truncate">
                            {invoice.invoiceNumber}
                        </span>
                        <StatusBadge status={invoice.status} />
                        <span className="text-sm font-semibold text-gray-900 tabular-nums ml-auto shrink-0">
                            {money(invoice.amount)}
                        </span>
                    </div>
                    <div className="mt-1.5 flex flex-col gap-0.5 text-[11px] text-gray-400">
                        <span>
                            Installment {invoice.installmentIndex + 1} of {charge.installments}
                        </span>
                        <span>Due {fmtDate(invoice.dueDate)}</span>
                        {invoice.paidAt && (
                            <span className="text-green-600">
                                Paid {fmtDate(invoice.paidAt)}
                                {daysLate(invoice.dueDate, invoice.paidAt) > 0 && (
                                    <span className="text-red-500">
                                        {' '}
                                        · {daysLate(invoice.dueDate, invoice.paidAt)} day
                                        {daysLate(invoice.dueDate, invoice.paidAt) === 1
                                            ? ''
                                            : 's'}{' '}
                                        late
                                    </span>
                                )}
                            </span>
                        )}
                    </div>
                </button>
            ))}
        </div>
    );
}

// ── Confirm modals (above the drawer, hence z-[60]) ─────────────────────
// CancelChargeModal is exported so the charges list row menu can reuse it.

export function CancelChargeModal({
    onConfirm,
    onClose,
    isPending
}: {
    onConfirm: (reason: string) => void;
    onClose: () => void;
    isPending: boolean;
}) {
    useEscapeKey(onClose, !isPending);

    const [reason, setReason] = useState('');
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex justify-center items-center">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-[440px] p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-2">Cancel Charge</h2>
                <p className="text-sm text-gray-600 mb-4">
                    The charge will be marked as canceled and can no longer be processed.
                </p>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Reason *</label>
                <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                    autoFocus
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                />
                <div className="flex justify-end items-center gap-3 mt-6">
                    <button
                        type="button"
                        onClick={onClose}
                        className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm"
                    >
                        Keep charge
                    </button>
                    <button
                        type="button"
                        onClick={() => onConfirm(reason.trim())}
                        disabled={isPending || !reason.trim()}
                        className="bg-red-700 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-red-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isPending ? 'Canceling…' : 'Cancel charge'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function ProcessChargeModal({
    charge,
    onConfirm,
    onClose,
    isPending
}: {
    charge: ChargeDetail;
    onConfirm: () => void;
    onClose: () => void;
    isPending: boolean;
}) {
    useEscapeKey(onClose, !isPending);
    const { usedCents, returnedCents } = allocateCredits(
        charge.oneTimeDiscounts,
        Math.round(Number(charge.totalAmount) * 100)
    );
    const settlesAtZero = settlesWithCredits(
        usedCents,
        Math.round(Number(charge.invoiceTotal) * 100)
    );

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex justify-center items-center">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-[440px] p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-2">Process Charge</h2>
                <div className="flex flex-col gap-4">
                    {settlesAtZero ? (
                        <p className="text-sm text-gray-600">
                            One-time discounts cover this charge in full. It is recorded as one paid{' '}
                            <span className="font-semibold">$0.00</span> invoice for{' '}
                            <span className="font-semibold">{charge.practice.name}</span>; nothing
                            is sent to Bill.com and the practice gets no email.
                        </p>
                    ) : (
                        <p className="text-sm text-gray-600">
                            This creates{' '}
                            <span className="font-semibold">
                                {charge.installments} invoice{charge.installments !== 1 ? 's' : ''}
                            </span>{' '}
                            in Bill.com for{' '}
                            <span className="font-semibold">{charge.practice.name}</span> totalling{' '}
                            <span className="font-semibold">{money(charge.invoiceTotal)}</span>, and
                            Bill.com emails them to the practice.
                        </p>
                    )}
                    {returnedCents > 0 && (
                        <p className="text-sm text-gray-600">
                            <span className="font-semibold">{formatMoneyCents(returnedCents)}</span>{' '}
                            of linked credit isn&apos;t needed and will be returned as credit.
                        </p>
                    )}
                    {!settlesAtZero && (
                        <UnusedCreditsWarning
                            charge={charge}
                            action="You can still create the invoices without them."
                        />
                    )}
                </div>
                <div className="flex justify-end items-center gap-3 mt-6">
                    <button
                        type="button"
                        onClick={onClose}
                        className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={isPending}
                        className="bg-gray-800 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isPending
                            ? 'Processing…'
                            : settlesAtZero
                              ? 'Settle as paid'
                              : 'Create invoices'}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── Drawer ──────────────────────────────────────────────────────────────

interface ChargeDrawerProps {
    chargeId: string;
    onClose: () => void;
    // Shown as a back chevron when the drawer was opened from another drawer.
    onBack?: () => void;
}

export function ChargeDrawer({ chargeId, onClose, onBack }: ChargeDrawerProps) {
    const { showToast } = useToast();
    const { hasPermission } = useMyPermissions();
    const canEditBilling = hasPermission('billing.update');
    const { charge, isLoading, isError } = useCharge(chargeId);
    const { mutateAsync: processCharge, isPending: isProcessing } = useProcessCharge();
    const { mutateAsync: cancelCharge, isPending: isCanceling } = useCancelCharge();
    const { mutateAsync: retryCharge, isPending: isRetrying } = useRetryCharge();

    const [showProcessModal, setShowProcessModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showSendPoModal, setShowSendPoModal] = useState(false);
    const [timelineMode, setTimelineMode] = useState<TimelineMode>('activity');

    const handleProcess = async () => {
        try {
            const { message, type } = chargeResultToast(
                await processCharge({ id: chargeId }),
                'process'
            );
            showToast(message, type);
            setShowProcessModal(false);
        } catch (error) {
            showToast(apiErrorMessage(error, 'Failed to process charge'), 'error');
        }
    };

    const handleCancel = async (reason: string) => {
        try {
            await cancelCharge({ id: chargeId, reason });
            showToast('Charge canceled', 'success');
            setShowCancelModal(false);
        } catch (error) {
            showToast(apiErrorMessage(error, 'Failed to cancel charge'), 'error');
        }
    };

    const handleRetry = async () => {
        try {
            const { message, type } = chargeResultToast(
                await retryCharge({ id: chargeId }),
                'retry'
            );
            showToast(message, type);
        } catch (error) {
            showToast(apiErrorMessage(error, 'Failed to retry charge'), 'error');
        }
    };

    if (isLoading) {
        return (
            <Drawer title="Charge" onClose={onClose} onBack={onBack}>
                <div className="flex items-center justify-center gap-2 text-gray-400 py-16">
                    <div className="w-4 h-4 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
                    <span className="text-sm">Loading charge…</span>
                </div>
            </Drawer>
        );
    }

    if (isError || !charge) {
        return (
            <Drawer title="Charge" onClose={onClose} onBack={onBack}>
                <p className="text-sm text-gray-500 py-16 text-center">Charge not found.</p>
            </Drawer>
        );
    }

    const canCancel = ['pending', 'error'].includes(charge.status);
    const isOrthopulseCharge = charge.lineItems.some((li) =>
        isOrthopulseProductName(li.productName)
    );
    // A purchase order row only exists once a dispatch has already succeeded,
    // so a re-send attempt beyond this local check is still validated (and can
    // be forced) by the backend's own already-ordered block.
    const canSendPurchaseOrder =
        canEditBilling &&
        isOrthopulseCharge &&
        Boolean(charge.patientId) &&
        charge.status !== 'canceled' &&
        charge.purchaseOrders.length === 0;

    return (
        <>
            <Drawer
                title={`Charge #${shortRef(charge.id)}`}
                badge={<StatusBadge status={charge.status} />}
                subtitle={charge.description ?? charge.lineItems[0]?.productName ?? '-'}
                onClose={onClose}
                onBack={onBack}
                // A stacked overlay owns Escape for the top layer; without this the
                // drawer's own listener fires on the same keypress and closes it too.
                closeDisabled={
                    showProcessModal || showCancelModal || showEditModal || showSendPoModal
                }
            >
                {charge.status === 'error' && charge.error && (
                    <ErrorAlert
                        message={charge.error}
                        onRetry={() => {
                            void handleRetry();
                        }}
                        isRetrying={isRetrying}
                        canEditBilling={canEditBilling}
                    />
                )}

                <DrawerSection title="Summary">
                    <div className="flex flex-col">
                        <KVRow k="Practice">
                            {charge.practice.name}
                            {charge.practice.location && (
                                <span className="text-gray-400"> · {charge.practice.location}</span>
                            )}
                        </KVRow>
                        {charge.practice.doctorName && (
                            <KVRow k="Doctor">Dr. {charge.practice.doctorName}</KVRow>
                        )}
                        <KVRow k="Patient">
                            <EditablePatient charge={charge} canEditBilling={canEditBilling} />
                        </KVRow>
                        <KVRow k="Created by">
                            <span className="inline-flex items-center gap-2">
                                {charge.createdByName ??
                                    (charge.createdVia === 'system' ? 'Automation' : '-')}
                                <SourceChip source={charge.createdVia} />
                            </span>
                        </KVRow>
                        <KVRow k="Payment terms">
                            {charge.paymentTerms.map((d) => `Net ${d}`).join(' / ')} ·{' '}
                            {charge.installments}×
                        </KVRow>
                        <KVRow k="Created">{fmtDateTime(charge.createdAt)}</KVRow>
                    </div>
                </DrawerSection>

                {charge.patientId && (
                    <DrawerSection title="Other charges for this patient" collapsible>
                        <PatientOtherCharges charge={charge} />
                    </DrawerSection>
                )}

                {charge.patientId && (
                    // Open by default (case context is needed for most billing
                    // decisions); collapsible to reclaim vertical space.
                    <DrawerSection
                        title="Patient timeline"
                        collapsible
                        action={
                            <TimelineModeToggle mode={timelineMode} onChange={setTimelineMode} />
                        }
                    >
                        <PatientTimelinePreview patientId={charge.patientId} mode={timelineMode} />
                    </DrawerSection>
                )}

                <DrawerSection title="Line items">
                    <LineItemsList charge={charge} />
                </DrawerSection>

                <DrawerSection
                    title={`One-time discounts${charge.oneTimeDiscounts.length ? ` (${charge.oneTimeDiscounts.length})` : ''}`}
                >
                    <UnusedCreditsWarning
                        key={charge.id}
                        charge={charge}
                        action="Edit the charge to apply them."
                    />
                    <AppliedOneTimeDiscounts charge={charge} />
                </DrawerSection>

                <DrawerSection title="Amount">
                    <ChargeBreakdown charge={charge} />
                </DrawerSection>

                <DrawerSection
                    title={`Invoices${charge.invoices.length ? ` (${charge.invoices.length})` : ''}`}
                >
                    <InvoicesList charge={charge} />
                </DrawerSection>

                <DrawerSection
                    title={`Purchase orders${charge.purchaseOrders.length ? ` (${charge.purchaseOrders.length})` : ''}`}
                    action={
                        canSendPurchaseOrder ? (
                            <button
                                type="button"
                                onClick={() => setShowSendPoModal(true)}
                                className="text-xs font-semibold text-gray-600 hover:text-gray-900 transition-colors"
                            >
                                Send purchase order
                            </button>
                        ) : undefined
                    }
                >
                    <ChargePurchaseOrders charge={charge} />
                </DrawerSection>

                <DrawerSection title="Activity">
                    <EventTimeline events={charge.events} />
                </DrawerSection>

                {canEditBilling && (charge.status === 'pending' || canCancel) && (
                    <div className="flex items-center gap-2.5 pt-1 pb-2">
                        {charge.status === 'pending' && (
                            <button
                                type="button"
                                onClick={() => setShowProcessModal(true)}
                                className="bg-black text-white font-semibold px-5 py-2 rounded-lg text-sm hover:bg-gray-900 transition-colors"
                            >
                                Process charge
                            </button>
                        )}
                        {charge.status === 'error' && (
                            <button
                                type="button"
                                onClick={() => {
                                    void handleRetry();
                                }}
                                disabled={isRetrying}
                                className="flex items-center gap-2 bg-black text-white font-semibold px-5 py-2 rounded-lg text-sm hover:bg-gray-900 transition-colors disabled:opacity-50"
                            >
                                <ArrowPathIcon className="h-4 w-4" />
                                {isRetrying ? 'Retrying…' : 'Retry charge'}
                            </button>
                        )}
                        {charge.status === 'pending' && (
                            <button
                                type="button"
                                onClick={() => setShowEditModal(true)}
                                className="flex items-center gap-2 bg-white text-gray-800 border border-gray-300 font-semibold px-4 py-2 rounded-lg text-sm hover:bg-gray-50 transition-colors"
                            >
                                <PencilIcon className="h-4 w-4" />
                                Edit charge
                            </button>
                        )}
                        {canCancel && (
                            <button
                                type="button"
                                onClick={() => setShowCancelModal(true)}
                                className="flex items-center gap-2 text-red-600 font-semibold px-3 py-2 rounded-lg text-sm hover:bg-red-50 transition-colors ml-auto"
                            >
                                <NoSymbolIcon className="h-4 w-4" />
                                Cancel charge
                            </button>
                        )}
                    </div>
                )}
            </Drawer>

            {showProcessModal && (
                <ProcessChargeModal
                    charge={charge}
                    onConfirm={() => {
                        void handleProcess();
                    }}
                    onClose={() => setShowProcessModal(false)}
                    isPending={isProcessing}
                />
            )}
            {showCancelModal && (
                <CancelChargeModal
                    onConfirm={(reason) => {
                        void handleCancel(reason);
                    }}
                    onClose={() => setShowCancelModal(false)}
                    isPending={isCanceling}
                />
            )}
            {showEditModal && (
                <CreateChargeModal
                    editCharge={charge}
                    onClose={() => setShowEditModal(false)}
                    onUpdated={() => setShowEditModal(false)}
                    onSuccess={() => setShowEditModal(false)}
                />
            )}
            {showSendPoModal && charge.patientId && (
                <SendPurchaseOrderModal
                    patientId={charge.patientId}
                    chargeId={charge.id}
                    onClose={() => setShowSendPoModal(false)}
                />
            )}
        </>
    );
}
